/**
 * Centralized storage service for the Novata Excel Plugin.
 *
 * Sensitive data (tokens, user info) is stored in OfficeRuntime.storage when
 * available.  When the Office runtime is not present the service falls back to
 * **sessionStorage** (not localStorage) so that credentials are automatically
 * cleared when the browser tab is closed, reducing the XSS exposure window.
 *
 * An additional base-64 encoding layer is applied to sensitive values in the
 * session-storage fallback so they are not stored as plain-text JSON.
 *
 * Non-sensitive configuration data (selected environment, organisation ID)
 * uses localStorage as a fallback so that it persists across sessions.
 */
import logger from "../../logger";

// ── Helpers ──────────────────────────────────────────────────────────────────

const ENCODING_PREFIX = "enc:";

function encode(value) {
  try {
    return ENCODING_PREFIX + btoa(unescape(encodeURIComponent(value)));
  } catch {
    return value;
  }
}

function decode(value) {
  if (typeof value !== "string" || !value.startsWith(ENCODING_PREFIX)) {
    return value;
  }
  try {
    return decodeURIComponent(escape(atob(value.slice(ENCODING_PREFIX.length))));
  } catch {
    return value;
  }
}

function isOfficeRuntimeAvailable() {
  return typeof OfficeRuntime !== "undefined" && OfficeRuntime.storage;
}

// ── Sensitive storage (tokens, user info) ────────────────────────────────────

/**
 * Store a sensitive value.
 * Primary: OfficeRuntime.storage
 * Fallback: sessionStorage with base-64 encoding
 */
export async function setSensitiveItem(key, value) {
  try {
    if (isOfficeRuntimeAvailable()) {
      await OfficeRuntime.storage.setItem(key, value);
    } else {
      sessionStorage.setItem(key, encode(value));
    }
  } catch (err) {
    logger.error("Failed to store sensitive item: %s", err?.message);
    throw err instanceof Error ? err : new Error(String(err));
  }
}

/**
 * Retrieve a sensitive value.
 */
export async function getSensitiveItem(key) {
  try {
    if (isOfficeRuntimeAvailable()) {
      return await OfficeRuntime.storage.getItem(key);
    }
    const raw = sessionStorage.getItem(key);
    return raw ? decode(raw) : null;
  } catch (err) {
    logger.error("Failed to read sensitive item: %s", err?.message);
    return null;
  }
}

/**
 * Remove a sensitive value from all possible locations.
 */
export async function removeSensitiveItem(key) {
  try {
    if (isOfficeRuntimeAvailable()) {
      await OfficeRuntime.storage.removeItem(key);
    }
    // Always attempt removal from session + local to ensure clean state
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* noop */
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  } catch (err) {
    logger.error("Failed to remove sensitive item: %s", err?.message);
  }
}

// ── Regular storage (environment, organisation, non-secret config) ───────────

/**
 * Store a non-sensitive configuration value.
 * Primary: OfficeRuntime.storage
 * Fallback: localStorage (persistence across sessions is acceptable)
 */
export async function setItem(key, value) {
  try {
    if (isOfficeRuntimeAvailable()) {
      await OfficeRuntime.storage.setItem(key, value);
    } else {
      localStorage.setItem(key, value);
    }
  } catch (err) {
    logger.error("Failed to store item: %s", err?.message);
  }
}

/**
 * Retrieve a non-sensitive configuration value.
 */
export async function getItem(key) {
  try {
    if (isOfficeRuntimeAvailable()) {
      return await OfficeRuntime.storage.getItem(key);
    }
    return localStorage.getItem(key);
  } catch (err) {
    logger.error("Failed to read item: %s", err?.message);
    return null;
  }
}

/**
 * Remove a non-sensitive configuration value.
 */
export async function removeItem(key) {
  try {
    if (isOfficeRuntimeAvailable()) {
      await OfficeRuntime.storage.removeItem(key);
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  } catch (err) {
    logger.error("Failed to remove item: %s", err?.message);
  }
}

// ── Bulk operations ──────────────────────────────────────────────────────────

/**
 * Clear all sensitive keys used by the authentication layer.
 */
export async function clearAllSensitiveData() {
  const sensitiveKeys = [
    "authTokens",
    "oktaTokens",
    "userInfo",
    "okta-token-storage",
    "okta-cache-storage",
    "okta-shared-transaction-storage",
  ];

  for (const key of sensitiveKeys) {
    await removeSensitiveItem(key);
  }

  // Also clear session-only Okta state
  const sessionOnlyKeys = [
    "okta-oauth-state",
    "okta-oauth-nonce",
    "okta-oauth-pkce",
    "okta-oauth-redirect-params",
    "okta-oauth-code-verifier",
  ];

  for (const key of sessionOnlyKeys) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* noop */
    }
  }
}

/**
 * Return a storage adapter object (for organization service compatibility).
 */
export function getStorageAdapter() {
  return {
    getItem: async (key) => await getItem(key),
    setItem: async (key, value) => await setItem(key, value),
    removeItem: async (key) => await removeItem(key),
  };
}
